import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { CacheService, ParameterHandlerService } from 'projects/common/src/app/services';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { IShift } from 'src/app/modules/core/interfaces';
import { IMachineStatus } from 'src/app/modules/widget/interfaces/machine-status';
import { firstLetterUpper } from 'projects/common/src/app/services/utils/utilities';
import { InputDateFormatPipe } from 'src/app/modules/core/pipes/input-date-format.pipe';
import { ISession } from 'projects/common/src/app/interfaces/authentication';
import { PermissionService } from 'src/app/modules/core/services/parameter/permission/permission.service';
import { IMachineContext } from '../../../../../../../../projects/common/src/app/interfaces/machine-context';
import { COMPANIES_ID } from 'projects/common/src/app/enumerables/companies-id';
import { EPermissions } from 'projects/common/src/app/enumerables/permissions';
import { IPermission } from 'projects/common/src/app/interfaces/permission';
import { GoogleAnalyticsService } from 'src/app/modules/core/services/google/google-analytics.service';
import { Subscription } from 'rxjs';
import { AuthenticationService } from 'projects/authentication/src/app/services';

@Component({
  selector: 'app-status-modal-shell',
  templateUrl: './status-modal-shell.component.html',
  styleUrls: ['./status-modal-shell.component.scss'],
})
export class StatusModalShellComponent implements OnInit, OnDestroy {
  @Input()
  public machineStatus: IMachineStatus;

  public readonly PERMISSION_OBJECT = 'UBICACIÓN';

  public inputDate: string;
  public shift: IShift;
  public companyId: number;
  public shiftInformation: string;
  public hideTab: boolean;
  public isHcm: boolean;
  public isHcmCompany: boolean;
  public permissions: IPermission[] = [];
  public canModify: boolean;
  public showActionTabsValidator: boolean;

  public logoutSub: Subscription;

  constructor(
    public activeModal: NgbActiveModal,
    private parameterService: ParameterHandlerService,
    private cacheService: CacheService,
    private languageService: LanguageService,
    private inputDateFormatPipe: InputDateFormatPipe,
    private permissionService: PermissionService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private authService: AuthenticationService,
  ) {
    this.inputDate = this.parameterService.parameters.item('date').value;
    this.shift = this.parameterService.parameters.item('shift').value as IShift;
    this.companyId = this.cacheService.local('session').user.company.id;
    this.hideTab = false;
    this.isHcm = false;
    this.showActionTabsValidator = this.isToday();
  }

  ngOnInit() {
    this.loadMachineType();
    this.setupPermissions();
    this.setShiftInformation();
    // 👮🏽 subscribe to the token expired event to close the modal
    this.logoutSub = this.authService.tokenExpired$.subscribe(() => {
      this.activeModal.dismiss('logout');
    });
  }

  ngOnDestroy() {
    // Unsubscribe to avoid memory leaks
    if (this.logoutSub) {
      this.logoutSub.unsubscribe();
    }
  }

  private loadMachineType() {
    const allMachines = this.cacheService.session('machineCollection') as IMachineContext[];
    if (allMachines) {
      const machine = allMachines.find((m) => m.machineTypeId === this.machineStatus.machineTypeId);
      if (machine) {
        this.machineStatus.machineType = machine.name.toLowerCase();
      } else {
        console.warn('machineType not found based on machineTypeId:', this.machineStatus.machineTypeId);
      }
    } else {
      console.warn('machineCollection unavailable.');
    }
  }

  private setupPermissions() {
    this.isHcmCompany = this.companyId === COMPANIES_ID.HCM;
    this.permissions = this.permissionService.getPermissionsByObj(this.PERMISSION_OBJECT);
    this.canModify = this.permissions.some(({ permission }) => permission.toLowerCase() === EPermissions.MODIFY.toLowerCase());

    const session = this.cacheService.local('session') as ISession;
    const hasPermission = this.permissionService.havePermission(session.permissions);

    this.isHcm = this.isHcmCompany && this.canModify;
    this.hideTab = !(this.isHcmCompany || hasPermission);
  }

  private setShiftInformation() {
    const formattedDate = this.inputDateFormatPipe.transform(this.inputDate);
    const shiftName = this.shift.name.toLowerCase();
    const dayDetailMessage = this.languageService.message('day_detail').toLowerCase();
    const shiftPrefix = this.languageService.message('shift_');

    this.shiftInformation =
      shiftName === dayDetailMessage
        ? `${firstLetterUpper(this.shift.name)} - ${formattedDate}`
        : `${shiftPrefix} ${this.shift.name} - ${formattedDate}`;
  }

  public changeTab($event: any) {
    let customEvent = {};
    if ($event.index === 0) {
      customEvent = {
        tab_modal: 'asarco-summary-team',
      };
    }
    if ($event.index === 1) {
      customEvent = {
        tab_modal: 'asarco-change-status',
      };
    }
    if ($event.index === 2) {
      customEvent = {
        tab_modal: 'asarco-change-park',
      };
    }
    if ($event.index === 3) {
      customEvent = {
        tab_modal: 'asarco-feature-team',
      };
    }
    if ($event.index === 4) {
      customEvent = {
        tab_modal: 'asarco-change-ubication',
      };
    }
    this.googleAnalyticsService.sendCustomEvent('modal', customEvent);
  }

  handleImageError(event: Event) {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = '../../../../../../../assets/img/dashboard-lhd/onemine-default-default.svg';
  }

  onUpdateStatusMachine(updatedStatus: { statusImage: string }) {
    if (updatedStatus && updatedStatus.statusImage != null) {
      // Update the statusImage of the machine
      this.machineStatus.statusImage = updatedStatus.statusImage;
    } else {
      console.error('No se recibieron datos de actualización del estado o el statusImage es inválido');
    }
  }

  /**
   * * Check if the input date is today.
   * * This method compares the input date (in 'YYYY-MM-DD' format) with today's date.
   * * @returns {boolean} - Returns true if the input date is today, false otherwise.
   * * @example
   * * today's date: 2025-04-09
   * * this.inputDate: "2025-04-09"
   * * year = 2025, month = 4, day = 9
   * * today.getFullYear() = 2025
   * * today.getMonth() + 1 = 4 (because getMonth() returns 0-based month)
   * * today.getDate() = 9
   * * * Result: all parts match → returns true
   */
  private isToday(): boolean {
    const today = new Date();
    // Split the input date string ('YYYY-MM-DD') into [year, month, day] and convert each part to a number
    const [year, month, day] = this.inputDate.split('-').map(Number);
    // Compare the input date to today's date by checking year, month, and day individually
    return today.getFullYear() === year && today.getMonth() + 1 === month && today.getDate() === day;
  }
}
